// ═══════════════════════════════════════════════════════════════════
// Business Configuration (Apr28 v4)
// ═══════════════════════════════════════════════════════════════════
// Single source of truth for business-level constants like free shipping
// threshold, contact phone, etc. Previously these were hardcoded in
// multiple places with INCONSISTENT values (e.g. ₹399 in TrustBadgeRow,
// ₹999 in WhatsApp campaigns, ₹1000 in Header announcement).
//
// Default values are baked in below as constants so the app works even
// if admin Settings haven't been configured yet. The async helper
// `loadBusinessConfig()` fetches admin overrides from the backend and
// caches them in localStorage for 1 hour.
//
// Usage:
//   import { FREE_SHIPPING_THRESHOLD, freeShippingLabel } from '../utils/businessConfig';
//   <span>{freeShippingLabel('en')}</span>   // "Free shipping over ₹999"
//   <span>{freeShippingLabel('hi')}</span>   // "₹999 से ऊपर मुफ्त शिपिंग"
// ═══════════════════════════════════════════════════════════════════

// ─── Defaults (used until admin overrides via Settings → Business) ────
export const FREE_SHIPPING_THRESHOLD = 999;
export const COD_AVAILABLE = true;
export const RETURNS_DAYS = 7;

// ─── Localized labels ─────────────────────────────────────────────────
export const freeShippingLabel = (lang = 'en', threshold = FREE_SHIPPING_THRESHOLD) => {
  const t = getThreshold(threshold);
  return lang === 'hi'
    ? `₹${t} से ऊपर मुफ्त शिपिंग`
    : `Free shipping over ₹${t}`;
};

export const freeShippingShort = (lang = 'en', threshold = FREE_SHIPPING_THRESHOLD) => {
  const t = getThreshold(threshold);
  return lang === 'hi' ? `₹${t} से ऊपर` : `On orders above ₹${t}`;
};

export const announcementShipping = (lang = 'en', threshold = FREE_SHIPPING_THRESHOLD) => {
  const t = getThreshold(threshold);
  return lang === 'hi'
    ? `₹${t} से ऊपर मुफ्त शिपिंग`
    : `Free Shipping on orders above ₹${t}`;
};

// ─── Admin override fetcher (graceful fallback) ───────────────────────
// Fetches `business` settings from the backend if available. Caches the
// result in localStorage for 1 hour to avoid repeat calls on every page.
// Falls back silently to defaults if backend doesn't have the setting yet.
const CACHE_KEY = 'gg_business_config';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

let cachedConfig = null;

function getThreshold(passedThreshold) {
  // If caller passes an explicit value, use it. Otherwise pull from cache.
  if (typeof passedThreshold === 'number' && passedThreshold > 0) return passedThreshold;
  if (cachedConfig?.freeShippingThreshold) return cachedConfig.freeShippingThreshold;

  // Try localStorage on first sync read (page boot before async fetch lands)
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.value?.freeShippingThreshold && parsed.fetchedAt + CACHE_TTL_MS > Date.now()) {
        cachedConfig = parsed.value;
        return parsed.value.freeShippingThreshold;
      }
    }
  } catch (_) {}

  return FREE_SHIPPING_THRESHOLD;
}

export const loadBusinessConfig = async (apiBase) => {
  // Fast path: still-valid cache
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.fetchedAt + CACHE_TTL_MS > Date.now()) {
        cachedConfig = parsed.value;
        return parsed.value;
      }
    }
  } catch (_) {}

  // Slow path: fetch from backend (silent failure → defaults)
  try {
    const base = apiBase || process.env.REACT_APP_API_URL || '';
    const res = await fetch(`${base}/api/settings/business`, { cache: 'no-store' });
    if (!res.ok) throw new Error('not ok');
    const data = await res.json();
    const value = {
      freeShippingThreshold: data?.freeShippingThreshold || FREE_SHIPPING_THRESHOLD,
      codAvailable:          data?.codAvailable ?? COD_AVAILABLE,
      returnsDays:           data?.returnsDays || RETURNS_DAYS,
    };
    cachedConfig = value;
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ value, fetchedAt: Date.now() }));
    } catch (_) {}
    return value;
  } catch (_) {
    return {
      freeShippingThreshold: FREE_SHIPPING_THRESHOLD,
      codAvailable:          COD_AVAILABLE,
      returnsDays:           RETURNS_DAYS,
    };
  }
};

// Synchronous helper — returns whatever's in cache (or defaults).
// Used in render paths where we can't await.
export const getBusinessConfig = () => {
  if (cachedConfig) return cachedConfig;
  return {
    freeShippingThreshold: FREE_SHIPPING_THRESHOLD,
    codAvailable:          COD_AVAILABLE,
    returnsDays:           RETURNS_DAYS,
  };
};
